import { Schema } from './Schema';
import { Value } from './Value';
import { TypeMismatchError } from './error';
import { ResolveReason } from './generated/confidence/flags/resolver/v1/types';
const FLAG_PREFIX = 'flags/';
export var FlagResolution;
(function (FlagResolution) {
    function ready(context, response, applier) {
        return new ReadyFlagResolution(context, response, applier);
    }
    FlagResolution.ready = ready;
    function failed(context, code, message) {
        return new FailedFlagResolution(context, code, message);
    }
    FlagResolution.failed = failed;
})(FlagResolution || (FlagResolution = {}));
export class ReadyFlagResolution {
    context;
    applier;
    flags = new Map();
    resolveToken;
    constructor(context, resolveResponse, applier) {
        this.context = context;
        this.applier = applier;
        for (const resolvedFlag of resolveResponse.resolvedFlags) {
            const { flag, variant, value, reason, flagSchema } = resolvedFlag;
            const name = flag.slice(FLAG_PREFIX.length);
            const schema = flagSchema ? Schema.parse({ structSchema: flagSchema }) : Schema.ANY;
            this.flags.set(name, {
                schema,
                value: value,
                variant,
                reason: toEvaluationReason(reason),
                set shouldApply(value) {
                    resolvedFlag.shouldApply = value;
                },
                get shouldApply() {
                    return resolvedFlag.shouldApply;
                },
            });
        }
        this.resolveToken = base64FromBytes(resolveResponse.resolveToken);
    }
    evaluate(path, defaultValue) {
        try {
            const [name, ...steps] = path.split('.');
            const flag = this.flags.get(name);
            if (!flag) {
                return {
                    reason: 'ERROR',
                    value: defaultValue,
                    errorCode: 'FLAG_NOT_FOUND',
                    errorMessage: `Flag "${name}" not found`,
                };
            }
            const reason = flag.reason;
            if (reason === 'ERROR')
                throw new Error('Unknown resolve error');
            if (flag.shouldApply && this.applier) {
                this.applier?.(name);
                flag.shouldApply = false;
            }
            if (reason !== 'MATCH') {
                return {
                    reason,
                    value: defaultValue,
                };
            }
            const value = TypeMismatchError.hoist(name, () => Value.get(flag.value, ...steps));
            const schema = flag.schema.get(...steps);
            TypeMismatchError.hoist(['defaultValue', ...steps], () => {
                schema.assertAssignsTo(defaultValue);
            });
            return {
                reason,
                value,
                variant: flag.variant,
            };
        }
        catch (e) {
            return {
                reason: 'ERROR',
                value: defaultValue,
                errorCode: e instanceof TypeMismatchError ? 'TYPE_MISMATCH' : 'GENERAL',
                errorMessage: e.message ?? 'Unknown error',
            };
        }
    }
}
ReadyFlagResolution.prototype.state = 'READY';
export class FailedFlagResolution {
    context;
    code;
    message;
    constructor(context, code, message) {
        this.context = context;
        this.code = code;
        this.message = message;
    }
    evaluate(_path, defaultValue) {
        return {
            reason: 'ERROR',
            value: defaultValue,
            errorCode: this.code,
            errorMessage: this.message,
        };
    }
}
FailedFlagResolution.prototype.state = 'ERROR';
function toEvaluationReason(reason) {
    switch (reason) {
        case ResolveReason.RESOLVE_REASON_UNSPECIFIED:
            return 'UNSPECIFIED';
        case ResolveReason.RESOLVE_REASON_MATCH:
            return 'MATCH';
        case ResolveReason.RESOLVE_REASON_NO_SEGMENT_MATCH:
            return 'NO_SEGMENT_MATCH';
        case ResolveReason.RESOLVE_REASON_NO_TREATMENT_MATCH:
            return 'NO_TREATMENT_MATCH';
        case ResolveReason.RESOLVE_REASON_FLAG_ARCHIVED:
            return 'FLAG_ARCHIVED';
        case ResolveReason.RESOLVE_REASON_TARGETING_KEY_ERROR:
            return 'TARGETING_KEY_ERROR';
        case ResolveReason.RESOLVE_REASON_ERROR:
            return 'ERROR';
        default:
            return 'UNSPECIFIED';
    }
}
function base64FromBytes(arr) {
    if (globalThis.Buffer) {
        return globalThis.Buffer.from(arr).toString('base64');
    }
    const bin = [];
    arr.forEach(byte => {
        bin.push(globalThis.String.fromCharCode(byte));
    });
    return globalThis.btoa(bin.join(''));
}
//# sourceMappingURL=FlagResolution.js.map